import { FlexBox, IconButton, Text, Tooltip } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import type { SidebarMode } from './conversationSidebar.types';
import styles from './sidebarToggleTrigger.module.scss';

type SidebarToggleTriggerProps = {
  isMenuOpen: boolean;
  toggleSidebar: () => void;
  sidebarMode: SidebarMode;
  setSidebarMode: (mode: SidebarMode) => void;
};

export const SidebarToggleTrigger = ({
  isMenuOpen,
  toggleSidebar,
  sidebarMode,
  setSidebarMode,
}: SidebarToggleTriggerProps): JSX.Element => {
  const flags = useFeatureFlag(['conversation-right-sidebar-visibility']);

  const handleToggle = useCallback(() => {
    if (!isMenuOpen) {
      ampli.conversationSidebarOpened();
    } else {
      ampli.conversationSidebarClosed();
    }

    toggleSidebar();
  }, [isMenuOpen, toggleSidebar]);

  const tooltipContent = (
    <CommandKeybindingsTooltipContent commandName="conversation:toggleRightSidebar">
      {isMenuOpen ? 'Close' : 'Show'}
    </CommandKeybindingsTooltipContent>
  );

  return (
    <FlexBox
      alignItems="center"
      justifyContent="space-between"
      padding="100 300"
      className={styles.root}
    >
      {isMenuOpen && (
        <FlexBox
          justifyContent="space-between"
          alignItems="center"
          gap="300"
          flex="1"
          padding="0 100 0 0"
        >
          {sidebarMode === 'customization' ? (
            <Text.ExternalLink
              onClick={() => setSidebarMode('default')}
              className={styles.backLink}
            >
              {'\u2039'} Back
            </Text.ExternalLink>
          ) : (
            <>
              <Text.Paragraph variant="1">Details</Text.Paragraph>
              {flags.conversationRightSidebarVisibility && (
                <Text.ExternalLink
                  onClick={() => setSidebarMode('customization')}
                >
                  Customize
                </Text.ExternalLink>
              )}
            </>
          )}
        </FlexBox>
      )}

      <Tooltip content={tooltipContent}>
        <IconButton
          variant="tertiary"
          onClick={handleToggle}
          icon={isMenuOpen ? 'angle-double-right' : 'angle-double-left'}
        />
      </Tooltip>
    </FlexBox>
  );
};
